<template>
  <LoadingIndicator v-if="isLoading">Loading channel details...</LoadingIndicator>
  <div v-else-if="channelData" class="details-container">
    <div class="details-header">
      <h2>Channel Details</h2>
    </div>
    <div class="details-content">
      <div class="details-section">
        <h3>Information</h3>
        <div class="details-group half-width">
          <label>Name</label>
          <div class="details-value">{{ channelData.name }}</div>
        </div>

        <div class="details-group half-width">
          <label>Organization</label>
          <div class="details-value">
            <router-link 
              :to="`/organizations/${channelData.organization_id}`" 
              :title="`View Organization &quot;${channelData.organization_name}&quot;`"
              class="link-button"
            >
              {{ channelData.organization_name || 'View Organization' }}
            </router-link>
          </div>
        </div>

        <div class="details-group full-width">
          <label>Banner Image</label>
          <div class="details-value">
            <img v-if="channelData.banner_url" :src="channelData.banner_url" alt="Channel banner" style="max-width: 100%;">
            <span v-else>No banner image provided</span>
          </div>
        </div>

        <div class="details-group">
          <label>Description</label>
          <div class="details-value">{{ channelData.description }}</div>
        </div>
      </div>

      <div class="details-section">
        <h3>Stats</h3>
        <div class="details-group third-width">
          <label>Video Files</label>
          <div class="details-value">
            {{ channelData.video_files?.length || 0 }}
          </div>
        </div>
        <div class="details-group third-width">
          <label>Storage</label>
          <div class="details-value">
            {{ storageSize }}
          </div>
        </div>
      </div>

      <div class="details-actions">
        <router-link 
          :to="`/channels/${channelId}/edit`" 
          class="important-button big" 
          v-if="isSuperuser()"
        >
          Edit Channel
        </router-link>
        <button class="cancel-button medium" @click="goBack">Back</button>
      </div>
    </div>
  </div>

  <template v-if="isSuperuser() && channelData">
    <div class="details-container wide">
      <div class="details-header">
        <h2>Channel Video Files</h2>
      </div>

      <VideoFileList
        :channel-id="channelId" 
        :organization-id="channelData.organization_id"
      />
    </div>

    <div class="details-container wide">
      <div class="details-header">
        <h2>Channel Publishing</h2>
      </div>

      <VideoList
        :channel-id="channelId" 
        :organization-id="channelData.organization_id"
      />
    </div>
  </template> 
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import VideoList from '@/components/VideoList.vue';
import VideoFileList from '@/components/VideoFileList.vue';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError, formatFileSize } from '@/scripts/shared-utils';
import { getChannel, channelKeys } from '@/scripts/api/channelApi';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const route = useRoute();
const router = useRouter();

const channelId = computed(() => route.params.channel_id);

const { data: channelData, isLoading } = useQuery({
  queryKey: computed(() => channelKeys.getChannel(channelId.value)),
  queryFn: () => getChannel(channelId.value),
  enabled: computed(() => !!channelId.value),
  staleTime: 60000,
  onError: (error) => handleApiError(error, 'fetching channel data'),
});

const storageSize = computed(() => {
  if (!channelData.value?.video_files) return '-';
  let storage = 0;
  for (const file of channelData.value.video_files) {
    if (file.file_size && !isNaN(file.file_size)) {
      storage += Number(file.file_size);
    }
  }
  return formatFileSize(storage);
});

const goBack = () => {
  router.back();
};
</script>

<style scoped>
@import '@/styles/shared-details.css';
@import '@/styles/shared-list.css';
</style>
